import {useState, useCallback, useMemo} from "react";
import {Star, Pencil, Check} from "lucide-react";
import * as m from "@/paraglide/messages";
import {cn} from "@/lib/utils";
import {Button} from "@/components/ui/button";
import {ScrollArea} from "@/components/ui/scroll-area";
import {PersonaCard, PersonaCardSkeleton} from "@/components/personas/persona-card";
import {PersonaModal, type PersonaModalTab} from "@/components/personas/persona-modal";
import {ChartCard} from "@/components/widgets/chart-card";
import {SocialPostCard} from "@/components/widgets/social-post-card";
import {InfluenceAccountCard} from "@/components/influence-account-card";
import {SortableGrid} from "@/components/sortable";
import {extractAiPersona} from "@/hooks/use-ai-personas";
import {useFavorites, useDeleteFavorite, useReorderFavorites} from "@/hooks/use-favorites";
import {useSortableEditMode} from "@/hooks/use-sortable-edit-mode";
import type {AiPersona, AiPersonaResponse} from "@/lib/personas-api";
import type {FavoriteResponse} from "@/lib/favorites-api";
import type {WidgetResponse} from "@/lib/widgets-api";
import type {ChartWidget, PostWidget} from "@/lib/chat-runtime";

export function FavoritesPage() {
  // Fetch all favorites (personas, widgets, and accounts)
  const {data, isLoading} = useFavorites();
  const deleteMutation = useDeleteFavorite();
  const reorderMutation = useReorderFavorites();

  // Modal state
  const [selectedPersona, setSelectedPersona] = useState<AiPersona | null>(null);
  const [selectedAiPersonaId, setSelectedAiPersonaId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTab, setModalTab] = useState<PersonaModalTab>("chat");

  const favorites = useMemo(() => data?.favorites ?? [], [data?.favorites]);

  // Sortable edit mode hook
  const {
    isEditMode,
    isDragging,
    activeId,
    activeItem,
    toggleEditMode,
    sensors,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
  } = useSortableEditMode({
    items: favorites,
    onReorder: (favoriteIds) => reorderMutation.mutate(favoriteIds),
  });

  const handlePersonaClick = useCallback(
    (favorite: FavoriteResponse) => {
      if (isEditMode) return; // Disable click in edit mode
      if (!favorite.entity_data || favorite.entity_type !== "persona") return;

      // Type guard: entity_data is AiPersonaResponse when entity_type is "persona"
      const entityData = favorite.entity_data as AiPersonaResponse;
      const aiPersona = extractAiPersona(entityData);
      setSelectedPersona(aiPersona);
      setSelectedAiPersonaId(favorite.entity_id);
      setModalTab("details");
      setIsModalOpen(true);
    },
    [isEditMode]
  );

  const handlePersonaChatClick = useCallback(
    (favorite: FavoriteResponse) => {
      if (isEditMode) return; // Disable click in edit mode
      if (!favorite.entity_data || favorite.entity_type !== "persona") return;

      // Type guard: entity_data is AiPersonaResponse when entity_type is "persona"
      const entityData = favorite.entity_data as AiPersonaResponse;
      const aiPersona = extractAiPersona(entityData);
      setSelectedPersona(aiPersona);
      setSelectedAiPersonaId(favorite.entity_id);
      setModalTab("chat");
      setIsModalOpen(true);
    },
    [isEditMode]
  );

  const handleRemoveFromFavorites = useCallback(
    (favoriteId: string) => {
      deleteMutation.mutate(favoriteId);
    },
    [deleteMutation]
  );

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  // Find favorite by ID for the delete callback in SortableGrid
  const handleDeleteItem = useCallback(
    (itemId: string) => {
      const favorite = favorites.find((f) => f.id === itemId);
      if (favorite) {
        handleRemoveFromFavorites(favorite.id);
      }
    },
    [favorites, handleRemoveFromFavorites]
  );

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header */}
      <div className="shrink-0 p-6 pb-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-primary/10 flex h-10 w-10 items-center justify-center rounded-lg">
              <Star className="text-primary h-5 w-5" />
            </div>
            <div>
              <h1 className="text-xl font-semibold">{m.favorites_page_title()}</h1>
              <p className="text-muted-foreground text-sm">{m.favorites_page_description()}</p>
            </div>
          </div>

          {/* Edit Mode Toggle */}
          {Boolean(favorites.length) && (
            <Button
              variant={isEditMode ? "default" : "outline"}
              size="sm"
              onClick={toggleEditMode}
              className={cn("gap-2 transition-all duration-200", isEditMode && "shadow-md")}
            >
              {isEditMode ? (
                <>
                  <Check className="h-4 w-4" />
                  {m.favorites_edit_mode_done()}
                </>
              ) : (
                <>
                  <Pencil className="h-4 w-4" />
                  {m.favorites_edit_mode()}
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Content Area */}
      <ScrollArea className="min-h-0 flex-1">
        {/* Loading State */}
        {isLoading && (
          <div className="grid gap-4 lg:grid-cols-2 2xl:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <PersonaCardSkeleton
                key={i}
                style={{animationDelay: `${i * 100}ms`}}
                className="animate-in fade-in-0 slide-in-from-bottom-4 fill-mode-both"
              />
            ))}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && favorites.length === 0 && (
          <div className="flex min-h-[400px] flex-col items-center justify-center">
            {/* Decorative background */}
            <div className="pointer-events-none absolute inset-0 overflow-hidden">
              <div className="from-primary/5 via-primary/2 absolute -top-32 -left-32 h-64 w-64 rounded-full bg-radial-[at_center] to-transparent blur-3xl" />
              <div className="absolute -right-32 -bottom-32 h-64 w-64 rounded-full bg-radial-[at_center] from-purple-500/5 via-purple-500/2 to-transparent blur-3xl" />
            </div>

            <div className="relative flex flex-col items-center">
              <div
                className={cn(
                  "mb-6 flex h-20 w-20 items-center justify-center rounded-2xl",
                  "bg-muted/30 ring-border/30 ring-1 backdrop-blur-sm",
                  "animate-in zoom-in-50 fade-in-0 duration-500"
                )}
              >
                <Star className="text-muted-foreground h-10 w-10" />
              </div>

              <h3 className="animate-in fade-in-0 slide-in-from-bottom-2 fill-mode-both mb-2 text-center text-lg font-semibold delay-100 duration-500">
                {m.favorites_empty_title()}
              </h3>
              <p className="text-muted-foreground animate-in fade-in-0 slide-in-from-bottom-2 fill-mode-both mb-6 max-w-sm text-center text-sm delay-200 duration-500">
                {m.favorites_empty_description()}
              </p>
            </div>
          </div>
        )}

        {/* Favorites Grid with Drag and Drop */}
        {!isLoading && favorites.length > 0 && (
          <SortableGrid
            items={favorites}
            isEditMode={isEditMode}
            isDragging={isDragging}
            activeId={activeId}
            activeItem={activeItem}
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
            onDeleteItem={handleDeleteItem}
            isDeleting={deleteMutation.isPending}
            className="p-4 md:p-6"
            gridClassName="grid auto-rows-fr gap-4 lg:grid-cols-2 2xl:grid-cols-3"
            renderItem={(favorite, _index, {isEditMode: itemEditMode}) => {
              // Render account cards
              if (favorite.entity_type === "account") {
                return (
                  <InfluenceAccountCard entityId={favorite.entity_id} isEditMode={itemEditMode} />
                );
              }

              // For other types, require entity_data
              if (!favorite.entity_data) return null;

              // Render persona card
              if (favorite.entity_type === "persona") {
                // Type guard: entity_data is AiPersonaResponse when entity_type is "persona"
                const entityData = favorite.entity_data as AiPersonaResponse;
                const persona = extractAiPersona(entityData);

                return (
                  <PersonaCard
                    persona={persona}
                    onClick={() => handlePersonaClick(favorite)}
                    onChatClick={() => handlePersonaChatClick(favorite)}
                    isEditMode={itemEditMode}
                  />
                );
              }

              // Render widget cards (no favorite button - use edit mode delete instead)
              if (favorite.entity_type === "widget") {
                const widgetData = favorite.entity_data as WidgetResponse;

                if (widgetData.widget_type === "chart") {
                  return <ChartCard chart={widgetData.data as ChartWidget} />;
                }

                if (widgetData.widget_type === "post") {
                  return <SocialPostCard post={widgetData.data as PostWidget} />;
                }
              }

              return null;
            }}
          />
        )}
      </ScrollArea>

      {/* Persona Modal */}
      <PersonaModal
        persona={selectedPersona}
        isOpen={isModalOpen}
        onClose={handleModalClose}
        activeTab={modalTab}
        onTabChange={setModalTab}
        aiPersonaId={selectedAiPersonaId ?? undefined}
      />
    </div>
  );
}
